import { useId, useState } from 'react'

/* ============================================================================
   Chart primitives for the admin portal.

   Inline SVG, no library. Hosting sends a strict CSP and the whole point of a
   twelve-column chart is that it needs about eighty lines of geometry, not two
   hundred kilobytes of runtime.

   Two rules run through everything here:

   1. ONE AXIS. Two measures of different scale are two charts, never two
      y-scales on one. That is why `WeeklyColumns` plots a single series and the
      overview stacks three of them as small multiples.
   2. MARKS WEAR THE SERIES COLOUR, TEXT WEARS TEXT TOKENS. Signal Orange is
      `#FF7A2E` on Showtime and darkens to `#B84406` on Daylight — that is what
      `--accent-text` resolves to, and it is the only value that clears 3:1
      against a white panel. Never `--accent` for a mark that must be seen on
      both skins; never a colour on a label.
   ============================================================================ */

const VIEW_W = 340
const VIEW_H = 148
const PAD_LEFT = 26
const PAD_RIGHT = 4
const PAD_TOP = 14
/** Room for the x-axis band. Sizing the box without it clips the date row. */
const PAD_BOTTOM = 20

const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM

/** Rounds a ceiling up to something a person would choose for an axis tick. */
function niceCeiling(max: number): number {
  if (max <= 4) return 4
  const magnitude = 10 ** Math.floor(Math.log10(max))
  for (const step of [1, 2, 2.5, 5, 10]) {
    const candidate = step * magnitude
    if (candidate >= max) return candidate
  }
  return 10 * magnitude
}

/** A column with a 4px-rounded cap, square where it meets the baseline. */
function columnPath(x: number, y: number, width: number, baseline: number): string {
  const height = baseline - y
  const r = Math.min(4, width / 2, height)
  if (height <= 0) return ''
  return [
    `M${x},${baseline}`,
    `L${x},${y + r}`,
    `Q${x},${y} ${x + r},${y}`,
    `L${x + width - r},${y}`,
    `Q${x + width},${y} ${x + width},${y + r}`,
    `L${x + width},${baseline}`,
    'Z',
  ].join(' ')
}

export interface ColumnDatum {
  /** Millisecond timestamps bounding the bucket, half-open: [start, end). */
  start: number
  end: number
  value: number
}

/**
 * Twelve weekly counts, one series.
 *
 * A single series needs no legend — the heading names what is plotted, and a
 * one-swatch legend box would just restate it. Only the tallest column carries
 * a printed value; a number on every column is noise nobody reads.
 */
export function WeeklyColumns({
  heading,
  data,
  unit,
  emptyNote = 'Nothing in this window yet.',
}: {
  heading: string
  data: ColumnDatum[]
  unit: string
  emptyNote?: string
}) {
  const [hover, setHover] = useState<number | null>(null)
  const [showTable, setShowTable] = useState(false)
  const tableId = useId()

  const total = data.reduce((sum, point) => sum + point.value, 0)
  const peak = Math.max(...data.map((point) => point.value), 0)
  const top = niceCeiling(peak)

  const band = PLOT_W / Math.max(data.length, 1)
  // Capped, and never filling its slot — the leftover in the band is the air
  // that stops twelve columns reading as one solid block.
  const barW = Math.min(24, band - 10)
  const baseline = PAD_TOP + PLOT_H
  const peakIndex = data.findIndex((point) => point.value === peak && peak > 0)

  const yOf = (value: number) => baseline - (value / top) * PLOT_H
  const centreOf = (index: number) => PAD_LEFT + index * band + band / 2

  return (
    <figure className="panel stack" style={{ gap: 10, padding: '16px 18px', margin: 0 }}>
      <figcaption className="spread" style={{ alignItems: 'baseline', gap: 10 }}>
        <span className="stack" style={{ gap: 2 }}>
          <span className="t-eyebrow">{heading}</span>
          <span className="t-spec" style={{ fontSize: '0.82rem' }}>
            {total.toLocaleString()} {unit} · 12 weeks
          </span>
        </span>
        <button
          type="button"
          className="btn btn-quiet"
          style={{ padding: '2px 6px', fontSize: '0.7rem' }}
          aria-expanded={showTable}
          aria-controls={tableId}
          onClick={() => setShowTable((open) => !open)}
        >
          {showTable ? 'Chart' : 'Table'}
        </button>
      </figcaption>

      {total === 0 ? (
        // Holds the plot's height so an empty card doesn't shorten its row and
        // leave the grid ragged.
        <p className="t-caption" style={{ display: 'grid', placeItems: 'center', minHeight: 166 }}>
          {emptyNote}
        </p>
      ) : showTable ? (
        <div id={tableId} style={{ maxHeight: 190, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th className="t-eyebrow" style={cell('left')}>
                  Week of
                </th>
                <th className="t-eyebrow" style={cell('right')}>
                  {unit}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((point) => (
                <tr key={point.start}>
                  <td className="t-spec-caption" style={cell('left')}>
                    {axisDate(point.start)}
                  </td>
                  <td className="t-spec-caption" style={cell('right')}>
                    {point.value.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // The reserved strip along the top is where the tooltip lives, so it
        // never covers the heading above it or the columns below it.
        <div id={tableId} style={{ position: 'relative', paddingTop: 24 }}>
          <svg
            viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
            width="100%"
            role="img"
            aria-label={`${heading}: ${total} ${unit} over twelve weeks, peaking at ${peak}.`}
            style={{ display: 'block', overflow: 'visible' }}
            onMouseLeave={() => setHover(null)}
          >
            {/* Hairline, solid, one step off the surface. Never dashed. */}
            {[0, 0.5, 1].map((fraction) => (
              <line
                key={fraction}
                x1={PAD_LEFT}
                x2={VIEW_W - PAD_RIGHT}
                y1={yOf(top * fraction)}
                y2={yOf(top * fraction)}
                stroke="var(--hairline)"
                strokeWidth={1}
              />
            ))}

            {[0, top].map((value) => (
              <text
                key={value}
                x={PAD_LEFT - 6}
                y={yOf(value) + 3}
                textAnchor="end"
                fill="var(--text-tertiary)"
                style={tickType}
              >
                {compact(value)}
              </text>
            ))}

            {data.map((point, index) => {
              const x = centreOf(index) - barW / 2
              const isHover = hover === index
              return (
                <g key={point.start}>
                  <path
                    d={columnPath(x, yOf(point.value), barW, baseline)}
                    fill="var(--accent-text)"
                    opacity={hover === null || isHover ? 1 : 0.45}
                  />
                  {/* The hit target is the whole band, floor to ceiling — a
                      3px-tall column is impossible to hover otherwise. */}
                  <rect
                    x={PAD_LEFT + index * band}
                    y={PAD_TOP}
                    width={band}
                    height={PLOT_H}
                    fill="transparent"
                    onMouseEnter={() => setHover(index)}
                  />
                </g>
              )
            })}

            {peakIndex >= 0 && hover === null && (
              <text
                x={centreOf(peakIndex)}
                y={yOf(peak) - 5}
                // Anchored inward at the ends so the label can't hang off the
                // plot — a clipped number is worse than an unlabelled column.
                textAnchor={
                  peakIndex === 0 ? 'start' : peakIndex === data.length - 1 ? 'end' : 'middle'
                }
                fill="var(--text-primary)"
                style={tickType}
              >
                {peak.toLocaleString()}
              </text>
            )}

            <line
              x1={PAD_LEFT}
              x2={VIEW_W - PAD_RIGHT}
              y1={baseline}
              y2={baseline}
              stroke="var(--hairline)"
              strokeWidth={1}
            />

            {[0, Math.floor(data.length / 2), data.length - 1].map((index) => (
              <text
                key={index}
                x={centreOf(index)}
                y={baseline + 13}
                textAnchor={index === 0 ? 'start' : index === data.length - 1 ? 'end' : 'middle'}
                fill="var(--text-tertiary)"
                style={tickType}
              >
                {axisDate(data[index].start)}
              </text>
            ))}
          </svg>

          {hover !== null && (
            <div
              role="status"
              style={{
                position: 'absolute',
                // Parked in whichever corner is away from the pointer rather
                // than centred on the column: centring needs the tooltip's own
                // width to clamp against, and at the ends it always overhangs.
                // Nothing is lost — the hovered column is the only one at full
                // opacity, and the label names its dates outright.
                ...(hover < data.length / 2 ? { right: 0 } : { left: 0 }),
                top: 0,
                padding: '3px 9px',
                borderRadius: 'var(--radius-chip)',
                background: 'var(--panel-raised)',
                border: 'var(--hairline-width) solid var(--hairline)',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <span className="t-spec-caption faint">
                {axisDate(data[hover].start)}–{axisDate(data[hover].end - 1)}
              </span>
              <span className="t-spec-caption" style={{ marginLeft: 8 }}>
                {data[hover].value.toLocaleString()} {unit}
              </span>
            </div>
          )}
        </div>
      )}
    </figure>
  )
}

/**
 * A rate, as a bar you can compare across three rows at a glance.
 *
 * The track is the accent at wash strength — a lighter step of the same ramp,
 * so the unfilled remainder still reads as part of the same measure.
 */
export function Meter({
  label,
  fraction,
  caption,
}: {
  label: string
  fraction: number
  caption: string
}) {
  const pct = Math.max(0, Math.min(1, fraction))
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="spread" style={{ alignItems: 'baseline', gap: 10 }}>
        <span className="t-body">{label}</span>
        <span className="t-spec" style={{ fontSize: '0.95rem' }}>
          {Math.round(pct * 100)}%
        </span>
      </div>
      <div
        role="meter"
        aria-valuenow={Math.round(pct * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        style={{
          height: 8,
          borderRadius: 999,
          background: 'var(--accent-wash)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct * 100}%`,
            height: '100%',
            borderRadius: 999,
            background: 'var(--accent-text)',
            transition: 'width 240ms var(--ease)',
          }}
        />
      </div>
      <span className="t-spec-caption faint">{caption}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ shared */

const tickType: React.CSSProperties = {
  fontFamily: 'var(--font-spec)',
  fontSize: 9,
  fontVariantNumeric: 'tabular-nums',
}

function cell(align: 'left' | 'right'): React.CSSProperties {
  return {
    textAlign: align,
    padding: '5px 0',
    borderBottom: 'var(--hairline-width) solid var(--hairline)',
  }
}

/** Axis ticks compact past a thousand — "10,000" doesn't fit the left gutter. */
function compact(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

function axisDate(at: number): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(at))
}
